/**
 * /destinations/$destinationId/runs/$runId — the run detail (plan §7.6): the
 * tuning surface. Stats, degradations, and EVERY record with its status and
 * reason — candidates show the exact Slack payload that would be posted,
 * suppressed records show why. Polls while the run is in flight.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { PageHeader } from '@/components/patterns/page-header';
import { StatusPill, type StatusPillTone } from '@/components/patterns/status-pill';
import { Skeleton } from '@/components/ui/skeleton';
import { type DestinationRecord, destinationsApi } from '@/lib/api';
import { qk } from '@/lib/query-client';

const POLL_MS = 2_000;

function recordTone(status: DestinationRecord['status']): StatusPillTone {
  switch (status) {
    case 'candidate':
    case 'delivered':
      return 'active';
    case 'suppressed':
      return 'warning';
    case 'merged':
      return 'neutral';
  }
}

function RecordCard({ record }: { record: DestinationRecord }) {
  const p = record.payload;
  const rendered = p.rendered as { text?: string; blocks?: unknown[] } | undefined;
  return (
    <div className="rounded-xl border border-border p-5">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold">{p.title}</p>
          <p className="mt-1 text-[13px] text-faint">
            {[
              p.owner_hint && `owner: ${p.owner_hint}`,
              (p.due ?? p.due_hint) && `due: ${p.due ?? `"${p.due_hint}"`}`,
              p.client_hint && `client: ${p.client_hint}`,
              `confidence ${p.confidence.toFixed(2)}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
        <StatusPill tone={recordTone(record.status)}>
          {record.status === 'suppressed' && record.suppressedReason
            ? record.suppressedReason
            : record.status}
        </StatusPill>
      </div>
      {p.body_md ? <p className="mt-3 text-sm text-muted-foreground">{p.body_md}</p> : null}
      {rendered ? (
        <details className="mt-3">
          <summary className="cursor-pointer text-[13px] font-medium text-faint">
            Slack payload that would be posted
          </summary>
          <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-muted p-3 text-[12px]">
            {JSON.stringify(rendered, null, 2)}
          </pre>
        </details>
      ) : null}
      {record.mergedIntoId ? (
        <p className="mt-2 text-[13px] text-faint">merged into {record.mergedIntoId}</p>
      ) : null}
    </div>
  );
}

export function DestinationRunDetailPage() {
  const { destinationId = '', runId = '' } = useParams({ strict: false }) as {
    destinationId?: string;
    runId?: string;
  };

  const { data: run, isLoading } = useQuery({
    queryKey: [...qk.destinationRuns(destinationId), runId],
    queryFn: () => destinationsApi.getRun(destinationId, runId),
    refetchInterval: (query) => (query.state.data?.status === 'running' ? POLL_MS : false),
  });

  if (isLoading || !run) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[200px] w-full rounded-xl" />
      </div>
    );
  }

  const stats = run.stats as Record<string, number | undefined>;
  const statItems: [string, number | undefined][] = [
    ['messages', stats.messages_scanned],
    ['segments', stats.segments],
    ['candidates', stats.candidates],
    ['merged', stats.merged_in_batch],
    ['cross-run dups', stats.duplicate_cross_run],
    ['suppressed', stats.suppressed],
    ['capped', stats.capped],
    ['LLM tokens', stats.llm_tokens],
    ...(run.dryRun
      ? []
      : ([
          ['delivered', stats.delivered],
          ['already delivered', stats.skipped_already_delivered],
          ['webhook requests', stats.webhook_requests],
        ] as [string, number | undefined][])),
  ];

  return (
    <div>
      <Link
        to="/destinations/$destinationId"
        params={{ destinationId }}
        className="mb-4 -ml-2 inline-flex items-center gap-1 text-sm text-faint hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Destination
      </Link>

      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title={`${run.dryRun ? 'Dry run' : 'Run'} — ${new Date(run.startedAt).toLocaleString()}`}
          subtitle={
            run.windowStart && run.windowEnd
              ? `Window: ${new Date(run.windowStart).toLocaleString()} → ${new Date(run.windowEnd).toLocaleString()}`
              : undefined
          }
        />
        <StatusPill
          tone={
            run.status === 'succeeded' ? 'active' : run.status === 'failed' ? 'danger' : 'paused'
          }
        >
          {run.status === 'running' ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> running
            </span>
          ) : (
            run.status
          )}
        </StatusPill>
      </div>

      {run.error ? (
        <div className="mt-6 rounded-xl border border-destructive-border bg-destructive-bg px-4 py-3">
          <p className="text-sm text-destructive">{run.error}</p>
        </div>
      ) : null}

      {run.degradations.length > 0 ? (
        <div className="mt-6 rounded-xl border border-warning-border bg-warning-bg px-4 py-3">
          <p className="text-sm font-medium text-warning">
            Degraded — this run did less than it should have
          </p>
          <ul className="mt-1 list-inside list-disc text-sm text-warning">
            {run.degradations.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {statItems.map(([label, value]) => (
          <div key={label} className="rounded-xl border border-border px-4 py-3">
            <p className="text-[13px] text-faint">{label}</p>
            <p className="text-lg font-semibold">{(value ?? 0).toLocaleString()}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-10 text-[15px] font-semibold">
        Records <span className="font-normal text-faint">({run.records.length})</span>
      </h2>
      <p className="mt-1 text-[13px] text-faint">
        Everything the pipeline decided, kept or dropped, with the reason — this is how you tune
        prompts and thresholds before anything posts.
      </p>
      <div className="mt-4 flex flex-col gap-3">
        {run.records.map((record) => (
          <RecordCard key={record.id} record={record} />
        ))}
        {run.records.length === 0 && run.status === 'succeeded' ? (
          <p className="rounded-xl border border-border px-4 py-6 text-center text-sm text-faint">
            A quiet window — no commitments extracted.
          </p>
        ) : null}
      </div>
    </div>
  );
}
